import { ReactNode, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { DayOfWeek } from '@weareopenr/types';
import { TimetableScheduleNormalizationError } from '@weareopenr/common/build/src/validationHelper/timetable-schedule';
import {
  BreadcrumbHeading,
  Chip,
  DuplicateIcon,
  Info,
  Popover,
  RadioSelect,
  SnackbarStatus,
  Spinner,
  Typography,
  useSnackbar,
  useTray,
} from '@weareopenr/component-library';
import { Routes } from 'src/routes';
import { SpinnerContainer } from 'src/components/SpinnerContainer';
import { formatDate } from 'src/helpers/formatDate/formatDate';
import { getBrandId } from 'src/helpers/getBrandId/getBrandId';
import { useTimetable } from 'src/hooks/useTimetable';
import { useTimetableRangesEnabled } from 'src/hooks/useTimetableRangesEnabled';
import { usePermissions } from 'src/hooks/usePermissions/usePermissions';
import { useUpdateTimetable } from 'src/hooks/useUpdateTimetable';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { TimetableDay } from 'src/components/TimetableDay';
import { AddScheduleFormValues, AddScheduleTrayContent } from 'src/components/AddScheduleTrayContent';
import {
  DuplicateTimetableTrayContent,
  getNextOpenId,
  ICloseDuplicateTrayOptions,
} from 'src/components/DuplicateTimetableTray';
import { useMenus } from 'src/hooks/useMenus';
import { usePriceSchemes } from 'src/hooks/usePriceSchemes';
import { ITimetableDetail, ITimetableResponseBody, ITimetableSchedule } from 'src/api/getTimetable';
import { IMenu } from 'src/types/menu.types';
import { IPriceScheme } from 'src/types/price.types';
import { addScheduleOptimistically, AddScheduleInput } from 'src/utils/timetables/addScheduleOptimistically';
import { getChangedScheduleIds } from 'src/utils/timetables/getChangedScheduleIds';
import { filterMenusByRanges, TimetableEntityType } from 'src/utils/timetables/helpers';
import styles from './Timetable.module.scss';

const ENTITY_LABELS: Record<TimetableEntityType, string> = {
  menu: 'menu',
  priceScheme: 'price scheme',
};

const ADD_SCHEDULE_ERROR_MESSAGES: Record<TimetableScheduleNormalizationError, string> = {
  [TimetableScheduleNormalizationError.InvalidStartTime]: 'Enter a valid start time.',
  [TimetableScheduleNormalizationError.InvalidEndTime]: 'Enter a valid end time.',
  [TimetableScheduleNormalizationError.MissingEntity]: 'Select a menu or price scheme.',
  [TimetableScheduleNormalizationError.ConflictingEntity]: 'A schedule can only have one menu or price scheme.',
  [TimetableScheduleNormalizationError.DuplicateStartTime]: 'Another schedule on this day already starts at that time.',
  // addScheduleOptimistically always builds a contiguous 00:00-23:59 day itself, so this should
  // be unreachable in practice - kept as a defensive fallback rather than an uncaught crash.
  [TimetableScheduleNormalizationError.NonContiguousSchedule]: 'Something went wrong building this schedule.',
};

// Any time left uncovered by the chosen start/end falls back to the timetable's default menu or
// price scheme - see addScheduleOptimistically.
const getDefaultEntity = (
  entityType: TimetableEntityType,
  timetable: ITimetableDetail,
  menus: IMenu[],
  priceSchemes: IPriceScheme[],
): { id: string; name: string } => {
  if (entityType === 'menu') {
    const id = timetable.default_menu_id;
    return { id, name: menus.find((menu) => menu.menuId === id)?.name ?? 'Default menu' };
  }
  const id = timetable.default_price_scheme_id;
  return { id, name: priceSchemes.find((priceScheme) => priceScheme.id === id)?.name ?? 'Default price scheme' };
};

export enum TimetableView {
  MENUS_AND_PRICING = 'MENUS_AND_PRICING',
  MENUS_ONLY = 'MENUS_ONLY',
  PRICING_ONLY = 'PRICING_ONLY',
}

const TIMETABLE_VIEW_OPTIONS = [
  { id: TimetableView.MENUS_AND_PRICING, label: 'Menus & Pricing' },
  { id: TimetableView.MENUS_ONLY, label: 'Menus only' },
  { id: TimetableView.PRICING_ONLY, label: 'Pricing only' },
];

interface TimetableTooltipProps {
  label: string;
  children: ReactNode;
}

const TimetableTooltip = ({ label, children }: TimetableTooltipProps) => (
  <Popover.Root>
    <Popover.Trigger asChild>
      <button type="button" aria-label={label}>
        <Info />
      </button>
    </Popover.Trigger>
    <Popover.Content side="bottom" align="start" sideOffset={4}>
      <div className={styles['tooltip']}>
        <Typography colour="secondary" size="small">
          {children}
        </Typography>
      </div>
    </Popover.Content>
  </Popover.Root>
);

export const Timetable = () => {
  const { timetableId } = useParams<{ timetableId?: string }>();
  const navigate = useNavigate();
  const [timetableView, setTimetableView] = useState(TimetableView.MENUS_AND_PRICING);

  const { data, byDay, isLoading } = useTimetable(timetableId);
  const timetable = data?.data;
  const { isEnabled: rangesEnabled } = useTimetableRangesEnabled();
  const { canCreateTimetables, canUpdateTimetables } = usePermissions();
  const { openTray, closeTray, isTrayOpen } = useTray();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync: submitTimetableUpdate } = useUpdateTimetable(timetableId);
  const { data: menus = [], isLoading: isMenusLoading } = useMenus();
  const { data: priceSchemes = [], isLoading: isPriceSchemesLoading } = usePriceSchemes();
  const menusInRange = filterMenusByRanges(menus, timetable?.ranges ?? []);
  // The default menu/price scheme already covers any unscheduled time (see
  // addScheduleOptimistically), so it isn't offered as something to explicitly add.
  const selectableMenus = menusInRange.filter((menu) => menu.menuId !== timetable?.default_menu_id);
  const selectablePriceSchemes = priceSchemes.filter(
    (priceScheme) => priceScheme.id !== timetable?.default_price_scheme_id,
  );

  // Snapshot of the schedules from just before the tray opened, so an uncommitted live preview
  // can be reverted if the user cancels, dismisses the tray, or navigates away without saving.
  const originalSchedulesRef = useRef<ITimetableSchedule[] | null>(null);
  const isCommittedRef = useRef(false);
  // Bumped on every open so the tray always gets a fresh AddScheduleTrayContent instance - the
  // Tray keeps its content mounted while closing (no unmountOnExit), so reusing the same key
  // across opens would carry over the previous session's form state instead of resetting it.
  const trayInstanceIdRef = useRef(0);
  // Ids of schedules the current live preview added or altered, so their tiles can be
  // highlighted with a dashed border while the preview is showing.
  const [changedScheduleIds, setChangedScheduleIds] = useState<Set<string>>(new Set());

  const writeSchedules = (schedules: ITimetableSchedule[]) => {
    queryClient.setQueryData<ITimetableResponseBody>(
      [QueryKeys.timetable, getBrandId(), timetableId],
      (current) => current && { ...current, data: { ...current.data, schedules } },
    );
  };

  const revertUncommittedPreview = () => {
    if (originalSchedulesRef.current && !isCommittedRef.current) {
      writeSchedules(originalSchedulesRef.current);
    }
    originalSchedulesRef.current = null;
    isCommittedRef.current = false;
  };

  // Covers Cancel and the tray's own close button, both of which just flip `isTrayOpen`. Only
  // clears `changedScheduleIds` here, not in the unmount cleanup below, since setting state
  // during/after unmount would warn - the component won't render again anyway once it's gone.
  useEffect(() => {
    if (!isTrayOpen) {
      revertUncommittedPreview();
      setChangedScheduleIds(new Set());
    }
  }, [isTrayOpen]);

  // Covers navigating away from the page entirely, while the tray is still open.
  useEffect(() => {
    return () => {
      revertUncommittedPreview();
      closeTray();
    };
  }, []);

  const buildAddScheduleInput = (
    entityType: TimetableEntityType,
    values: AddScheduleFormValues,
  ): AddScheduleInput | null => {
    if (!timetable) return null;
    const defaultEntity = getDefaultEntity(entityType, timetable, menusInRange, priceSchemes);
    return {
      entityType,
      ...values,
      defaultEntityId: defaultEntity.id,
      defaultEntityName: defaultEntity.name,
      defaultMenuId: timetable.default_menu_id,
      defaultPriceSchemeId: timetable.default_price_scheme_id,
    };
  };

  const handleAddSchedulePreview = (entityType: TimetableEntityType, values: AddScheduleFormValues | null) => {
    if (!originalSchedulesRef.current) return;

    const input = values && buildAddScheduleInput(entityType, values);
    if (!input) {
      writeSchedules(originalSchedulesRef.current);
      setChangedScheduleIds(new Set());
      return;
    }

    const result = addScheduleOptimistically(originalSchedulesRef.current, input);
    if (result.success) {
      writeSchedules(result.schedules);
      setChangedScheduleIds(getChangedScheduleIds(originalSchedulesRef.current, result.schedules));
    } else {
      writeSchedules(originalSchedulesRef.current);
      setChangedScheduleIds(new Set());
    }
  };

  const handleAddScheduleSubmit = async (
    entityType: TimetableEntityType,
    values: AddScheduleFormValues,
  ): Promise<boolean> => {
    if (!originalSchedulesRef.current || !timetableId) return false;
    const input = buildAddScheduleInput(entityType, values);
    if (!input) return false;

    const result = addScheduleOptimistically(originalSchedulesRef.current, input);

    if (result.success === false) {
      showSnackbar({ status: SnackbarStatus.ERROR, message: ADD_SCHEDULE_ERROR_MESSAGES[result.error] });
      return false;
    }

    // Keep the optimistic preview showing while the PATCH is in flight.
    writeSchedules(result.schedules);

    try {
      const response = await submitTimetableUpdate(result.blocks);
      isCommittedRef.current = true;
      queryClient.setQueryData([QueryKeys.timetable, getBrandId(), timetableId], response);
      return true;
    } catch {
      showSnackbar({ status: SnackbarStatus.ERROR, message: 'Something went wrong whilst saving the schedule.' });
      writeSchedules(originalSchedulesRef.current);
      setChangedScheduleIds(new Set());
      return false;
    }
  };

  const handleAddSchedule = (entityType: TimetableEntityType, day: DayOfWeek) => {
    if (!timetable) return;
    const entityLabel = ENTITY_LABELS[entityType];

    // If a tray was already open with an uncommitted preview (e.g. the user pressed + on
    // another row without closing it first), `timetable.schedules` from this render may still
    // reflect that preview rather than the true original - fall back to the ref instead.
    const trueOriginalSchedules = originalSchedulesRef.current ?? timetable.schedules;
    revertUncommittedPreview();
    originalSchedulesRef.current = trueOriginalSchedules;
    setChangedScheduleIds(new Set());
    trayInstanceIdRef.current += 1;

    openTray(
      <AddScheduleTrayContent
        key={`${entityType}-${day}-${trayInstanceIdRef.current}`}
        entityType={entityType}
        initialDay={day}
        menus={selectableMenus}
        isMenusLoading={isMenusLoading}
        priceSchemes={selectablePriceSchemes}
        isPriceSchemesLoading={isPriceSchemesLoading}
        onPreview={(values) => handleAddSchedulePreview(entityType, values)}
        onSubmit={(values) => handleAddScheduleSubmit(entityType, values)}
      />,
      { title: `Add ${entityLabel}` },
    );
  };

  if (isLoading) {
    return (
      <SpinnerContainer data-testid="loadingIcon">
        <Spinner size="xlarge" colour="rainbow" />
      </SpinnerContainer>
    );
  }

  if (!timetableId || !timetable) return <Navigate to={Routes.TIMETABLES} replace />;

  const openDuplicateTray = () => {
    const onClose = (options?: ICloseDuplicateTrayOptions) => {
      closeTray();
      if (options?.duplicated) navigate(Routes.TIMETABLES);
    };

    openTray(
      <DuplicateTimetableTrayContent
        openId={getNextOpenId()}
        timetableId={timetable.timetable_id}
        sourceName={timetable.name}
        onClose={onClose}
      />,
      { title: 'Duplicate timetable' },
    );
  };

  return (
    <div className={styles['page-container']}>
      <BreadcrumbHeading
        breadcrumbs={[
          { path: Routes.TIMETABLES, label: 'Timetables' },
          { path: `${Routes.TIMETABLES}/${timetable.timetable_id}`, label: timetable.name },
        ]}
        subtitle={`Last updated at ${formatDate(new Date(timetable.update_date))}`}
        optionMenuItems={[
          {
            id: 'duplicate-timetable',
            name: 'Duplicate',
            icon: DuplicateIcon,
            isDisabled: !canCreateTimetables || isTrayOpen,
            onOptionClick: openDuplicateTray,
          },
        ]}
      />
      {rangesEnabled && (
        <div className={styles['ranges']}>
          <Typography size="small" className={styles['label']}>
            {timetable.ranges.length === 0 ? 'No Ranges' : 'Range(s) -'}
          </Typography>
          {timetable.ranges.map((range) => (
            <Chip key={range.range_id} label={range.name} icon="priceBand" backgroundColour="grape" />
          ))}
        </div>
      )}
      <div className={styles['toolbar']}>
        <RadioSelect
          label="Timetable View"
          name="timetable-view"
          options={[
            {
              options: TIMETABLE_VIEW_OPTIONS,
              selected: timetableView,
              onChange: (id) => setTimetableView(id as TimetableView),
            },
          ]}
        />
      </div>
      <div className={styles['list']}>
        <div className={styles['day-range']}>
          <div className={styles['day-range-start']}>
            <Typography size="small" className={styles['label']}>
              Day Start (00:00)
            </Typography>
            <TimetableTooltip label="Day start information">
              The timetable must cover a 24-hour period, which is determined by your brand&apos;s trading hours. Venue
              open and close are determined by your venue&apos;s opening hours in the Venue Details Page
            </TimetableTooltip>
          </div>
          <Typography size="small" className={`${styles['label']} ${styles['day-range-end']}`}>
            Day End (23:59)
          </Typography>
        </div>
        <div className={styles['days']}>
          {byDay.map((day) => (
            <TimetableDay
              key={day.day}
              day={day}
              view={timetableView}
              onAddSchedule={handleAddSchedule}
              isActionable={canUpdateTimetables && !isTrayOpen}
              changedScheduleIds={changedScheduleIds}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Timetable;
